import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MediaBackdrop, Sheet, SheetContent, SheetTitle } from "@nexus/ui";
import { DoubleDownButton } from "@/features/post-flight/components/DoubleDownButton";
import type { PostFlightOpportunity } from "@/features/post-flight/api/post-flight.api";
import { PostFlightWindowContext } from "@/features/persona-opportunities/components/shared/PostFlightWindowContext";
import { postFlightWindowContextFromListRow } from "@/features/persona-opportunities/lib/post-flight-opportunity-metrics";
import {
  adTitle,
  cleanReason,
  evidenceLabel,
  formatRatePair,
  isImageCreative,
  isRunning,
  prettyLabel,
  type PostFlightAd,
  type PostFlightWindow,
} from "@/features/post-flight/lib/post-flight-list";

const TYPE_CHIP: Record<string, string> = {
  winning_creative: "bg-success-subtle text-success-subtle-foreground",
  underexplored_winner: "bg-info-subtle text-info-subtle-foreground",
  emerging_creative: "bg-warning-subtle text-warning-subtle-foreground",
  strong_hook: "bg-warning-subtle text-warning-subtle-foreground",
};

interface AdDrawerProps {
  ad: PostFlightAd | null;
  windowDays: PostFlightWindow;
  listAsOf?: string | null;
  shouldPlayVideo: boolean;
  canDoubleDown?: boolean;
  isTypeDisabled?: boolean;
  onClose: () => void;
  onDoubleDown: () => void;
  onVideoStarted: () => void;
}

export function AdDrawer({
  ad,
  windowDays,
  listAsOf = null,
  shouldPlayVideo,
  canDoubleDown = true,
  isTypeDisabled = false,
  onClose,
  onDoubleDown,
  onVideoStarted,
}: AdDrawerProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!shouldPlayVideo) return;
    const video = videoRef.current;
    if (!video) return;
    void video.play().catch(() => undefined);
    onVideoStarted();
  }, [shouldPlayVideo, ad?.hash, onVideoStarted]);
  const lead = ad?.rows[0];
  const mediaRow = ad?.rows.find((row) => row.video_url);
  const mediaUrl = mediaRow?.video_url ?? null;
  const image = isImageCreative(mediaUrl, mediaRow?.object_type);
  const running = isRunning(lead?.active);
  const audience = audienceRows(t, lead);

  return (
    <Sheet open={ad !== null} onOpenChange={(open) => { if (!open) onClose(); }} modal={false}>
      <SheetContent aria-describedby={undefined} className="max-w-[720px] gap-0 overflow-hidden p-0">
        <div className="flex items-start gap-3 border-b border-border px-6 pt-5 pr-14 pb-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            <SheetTitle>{ad ? adTitle(ad) : ""}</SheetTitle>
            <p className="text-[12px] text-muted-foreground">
              {[
                running === null
                  ? null
                  : running
                    ? t("postFlight.card.running", "Running")
                    : t("postFlight.card.ended", "Ended"),
                typeof lead?.days_live === "number"
                  ? t("postFlight.card.daysLive", { count: lead.days_live })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {ad ? (
              <PostFlightWindowContext
                className="mt-1 flex flex-col gap-1 text-[12px] text-muted-foreground"
                data={postFlightWindowContextFromListRow(lead, listAsOf)}
                listAsOf={listAsOf}
                fallbackWindowDays={windowDays}
              />
            ) : null}
          </div>
        </div>
        {ad ? (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 sm:grid-cols-[200px_minmax(0,1fr)]">
            {mediaUrl && image ? (
              <div className="sticky top-0 isolate aspect-[9/16] overflow-hidden rounded-md">
                <img src={mediaUrl} alt="" className="h-full w-full object-contain" />
                <MediaBackdrop src={mediaUrl} />
              </div>
            ) : mediaUrl ? (
              <div className="sticky top-0 isolate aspect-[9/16] overflow-hidden rounded-md">
                <video ref={videoRef} src={mediaUrl} controls playsInline preload="metadata" className="h-full w-full object-contain" />
                <MediaBackdrop src={mediaUrl} type="video" />
              </div>
            ) : (
              <div
                className="sticky top-0 aspect-[9/16] rounded-md bg-muted"
                aria-label={t("postFlight.drawer.noVideo", "No video")}
              />
            )}
            <div className="flex min-w-0 flex-col gap-6">
              <section className="flex flex-col gap-3">
                <h3 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                  {t("postFlight.drawer.why", "Why")}
                </h3>
                {ad.rows.map((row) => (
                  <div key={`${ad.hash}-${row.source}-${row.opportunity_type}`} className="flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex h-5 items-center gap-1 rounded-sm px-1.5 text-[10px] ${TYPE_CHIP[row.opportunity_type] ?? "bg-muted text-foreground"}`}>
                        <span className="size-1.5 rounded-full bg-current" />
                        {t(`postFlight.types.${row.opportunity_type}`, row.opportunity_type)}
                      </span>
                      {evidenceLabel(row.evidence) ? (
                        <span className="text-[11px] text-muted-foreground">{evidenceLabel(row.evidence)}</span>
                      ) : null}
                    </div>
                    {cleanReason(row.reason) ? <p className="text-[13px] leading-snug">{cleanReason(row.reason)}</p> : null}
                  </div>
                ))}
              </section>
              <BaselineSection lead={lead} />
              {audience.length > 0 ? (
                <section className="flex flex-col gap-2">
                  <h3 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {t("postFlight.drawer.audience", "Audience")}
                  </h3>
                  {audience.map((row) => (
                    <div key={row.label} className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-[12px]">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span>{row.value}</span>
                    </div>
                  ))}
                </section>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end border-t border-border bg-muted px-6 py-4">
          <DoubleDownButton
            image={image}
            canDoubleDown={canDoubleDown}
            isTypeDisabled={isTypeDisabled}
            onClick={onDoubleDown}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BaselineSection({ lead }: { lead: PostFlightOpportunity | undefined }) {
  const { t } = useTranslation();
  if (!lead) return null;
  const verdict = (pct: number, higher: boolean) =>
    higher
      ? t("postFlight.drawer.higher", "{{pct}}% higher", { pct })
      : t("postFlight.drawer.lower", "{{pct}}% lower", { pct });
  const bars = [
    compareBar(t("postFlight.drawer.cpa", "CPA"), lead.cpa, lead.account_cpa, t("postFlight.drawer.account", "account"), true, false, verdict),
    compareBar(t("postFlight.drawer.thumbstop", "Thumbstop"), lead.thumbstop, lead.thumbstop_expected, t("postFlight.drawer.expected", "expected"), false, true, verdict),
    compareBar(t("postFlight.drawer.holdRate", "Hold rate"), lead.hold_rate, lead.account_hold_rate, t("postFlight.drawer.account", "account"), false, true, verdict),
  ].filter((bar): bar is CompareBar => bar !== null);
  if (bars.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] tracking-wide text-muted-foreground uppercase">
          {t("postFlight.drawer.baseline", "Against baseline")}
        </h3>
        <p className="flex items-center gap-3 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-2.5 rounded-sm bg-primary" />
            {t("postFlight.drawer.thisAd", "This ad")}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-2.5 rounded-sm bg-[var(--chart-2)]" />
            {t("postFlight.drawer.baselineName", "Baseline")}
          </span>
        </p>
      </div>
      {bars.map((bar) => (
        <div key={bar.label} className="flex flex-col gap-1">
          <div className="flex justify-between gap-2 text-[12px]">
            <span>{bar.label}</span>
            <span className={bar.good ? "text-success" : "text-destructive"}>{bar.verdict}</span>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-x-2 text-[12px] tabular-nums">
            <span className="h-2 rounded-sm bg-primary" style={{ width: bar.valueWidth }} />
            <span>{bar.valueText}</span>
            <span className="h-2 rounded-sm bg-[var(--chart-2)]" style={{ width: bar.baselineWidth }} />
            <span className="text-muted-foreground">{bar.baselineText}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

interface CompareBar {
  label: string;
  verdict: string;
  good: boolean;
  valueWidth: string;
  baselineWidth: string;
  valueText: string;
  baselineText: string;
}

function compareBar(
  label: string,
  value: number | null | undefined,
  baseline: number | null | undefined,
  baselineName: string,
  lowerIsBetter: boolean,
  asPercent: boolean,
  verdictFor: (pct: number, higher: boolean) => string,
): CompareBar | null {
  if (typeof value !== "number" || typeof baseline !== "number" || baseline === 0) return null;
  const max = Math.max(value, baseline);
  const delta = (value - baseline) / baseline;
  const higher = delta > 0;
  const money = (amount: number) => Math.round(amount).toLocaleString("en-US");
  const pair = asPercent ? formatRatePair(value, baseline) : null;
  return {
    label,
    verdict: verdictFor(Math.abs(Math.round(delta * 100)), higher),
    good: lowerIsBetter ? delta < 0 : delta > 0,
    valueWidth: `${(value / max) * 100}%`,
    baselineWidth: `${(baseline / max) * 100}%`,
    valueText: pair ? pair.value : money(value),
    baselineText: pair ? `${pair.baseline} ${baselineName}` : `${money(baseline)} ${baselineName}`,
  };
}

function audienceRows(
  t: (key: string, fallback: string) => string,
  lead: PostFlightOpportunity | undefined,
): Array<{ label: string; value: string }> {
  if (!lead) return [];
  return [
    { label: t("postFlight.drawer.persona", "Persona"), value: prettyLabel(lead.persona) },
    { label: t("postFlight.drawer.motivation", "Motivation"), value: prettyLabel(lead.motivation) },
    { label: t("postFlight.drawer.painPoint", "Pain point"), value: prettyLabel(lead.pain_point) },
  ].filter((row) => row.value.length > 0);
}
